import React, { useEffect, useMemo, useState } from 'react';
import { Hand, AlertTriangle, CheckCircle2, Radio, Lock } from 'lucide-react';
import { Layout } from '../components/Layout';
import { StatCard } from '../components/StatCard';
import { RealtimeLeadCard } from '../components/RealtimeLeadCard';
import { ClaimMonitorTable, ClaimMonitorRow } from '../components/ClaimMonitorTable';
import { useRole } from '../contexts/RoleContext';
import { REALTIME_LEADS, RealtimeLead, LeadState, CLAIM_WINDOW_HOURS, getLeadState, claimTimeRemaining, timeSinceContact } from '../data/realtimeLeads';
type QueueTab = 'available' | 'mine' | 'all_claims' | 'converted';
const USER_TABS: {
  id: QueueTab;
  label: string;
}[] = [{
  id: 'available',
  label: 'Available to Claim'
}, {
  id: 'mine',
  label: 'My Claims'
}, {
  id: 'converted',
  label: 'Loans Created'
}];

// Admins monitor every officer's claims instead of working a personal queue.
const ADMIN_TABS: {
  id: QueueTab;
  label: string;
}[] = [{
  id: 'available',
  label: 'Available to Claim'
}, {
  id: 'all_claims',
  label: 'All Claims'
}, {
  id: 'converted',
  label: 'Loans Created'
}];

// Signed-in user for this prototype, derived from the active role.
const CURRENT_USER: Record<string, {
  id: string;
  name: string;
}> = {
  officer: {
    id: 'officer1',
    name: 'John Officer'
  },
  manager: {
    id: 'manager1',
    name: 'Sarah Manager'
  },
  admin: {
    id: 'admin',
    name: 'System Admin'
  }
};
export function RealtimeLeads() {
  const {
    role
  } = useRole();
  const isAdmin = role === 'admin';
  const currentUser = CURRENT_USER[role] ?? CURRENT_USER.officer;
  const tabs = isAdmin ? ADMIN_TABS : USER_TABS;
  const [leads, setLeads] = useState<RealtimeLead[]>(REALTIME_LEADS);
  const [tab, setTab] = useState<QueueTab>('available');

  // Keep the active tab valid when the role (and therefore the tab set) changes.
  useEffect(() => {
    if (!tabs.some(t => t.id === tab)) setTab('available');
  }, [tabs, tab]);
  // Ticking clock so claim countdowns stay live and lapsed claims release themselves.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const withState = useMemo(() => leads.map(lead => ({
    lead,
    state: getLeadState(lead, now, currentUser.id)
  })), [leads, now, currentUser.id]);
  const isExpiringSoon = (lead: RealtimeLead) => claimTimeRemaining(lead, now) < 4 * 3600 * 1000;
  const counts = useMemo(() => {
    const available = withState.filter(l => l.state === 'available').length;
    const mine = withState.filter(l => l.state === 'claimed_by_me').length;
    const converted = withState.filter(l => l.state === 'loan_created').length;
    const allClaims = withState.filter(l => l.state === 'claimed_by_me' || l.state === 'claimed_by_other').length;
    // Officers track only their own expiring claims; admins track everyone's.
    const expiringSoon = withState.filter(l => (isAdmin ? l.state === 'claimed_by_me' || l.state === 'claimed_by_other' : l.state === 'claimed_by_me') && isExpiringSoon(l.lead)).length;
    return {
      available,
      mine,
      converted,
      allClaims,
      expiringSoon
    };
  }, [withState, now, isAdmin]);

  // Per-officer claim roll-up for the admin monitoring view.
  const monitorRows = useMemo<ClaimMonitorRow[]>(() => {
    if (!isAdmin) return [];
    const byOwner = new Map<string, ClaimMonitorRow>();
    const ensure = (id: string, name: string) => {
      if (!byOwner.has(id)) {
        byOwner.set(id, {
          ownerId: id,
          ownerName: name,
          activeClaims: 0,
          expiringSoon: 0,
          loansCreated: 0,
          returned: 0
        });
      }
      return byOwner.get(id) as ClaimMonitorRow;
    };
    withState.forEach(({
      lead,
      state
    }) => {
      if (!lead.claimedBy || !lead.claimedByName) return;
      const row = ensure(lead.claimedBy, lead.claimedByName);
      if (state === 'loan_created') {
        row.loansCreated += 1;
      } else if (state === 'claimed_by_me' || state === 'claimed_by_other') {
        row.activeClaims += 1;
        if (isExpiringSoon(lead)) row.expiringSoon += 1;
      }
      row.returned += lead.releaseCount;
    });
    return [...byOwner.values()].sort((a, b) => b.activeClaims - a.activeClaims);
  }, [withState, isAdmin, now]);
  const visible = useMemo(() => {
    const matches = (state: LeadState) => {
      if (tab === 'available') return state === 'available' || state === 'claimed_by_other';
      if (tab === 'mine') return state === 'claimed_by_me';
      if (tab === 'all_claims') return state === 'claimed_by_me' || state === 'claimed_by_other';
      return state === 'loan_created';
    };
    return withState.filter(({
      state
    }) => matches(state)).sort((a, b) => {
      if (tab === 'mine' || tab === 'all_claims') {
        // Soonest to expire first - those need a loan created now.
        return claimTimeRemaining(a.lead, now) - claimTimeRemaining(b.lead, now);
      }
      if (tab === 'available') {
        // Claimable leads first, then freshest contact first.
        if (a.state !== b.state) return a.state === 'available' ? -1 : 1;
        return timeSinceContact(a.lead, now) - timeSinceContact(b.lead, now);
      }
      return timeSinceContact(a.lead, now) - timeSinceContact(b.lead, now);
    });
  }, [withState, tab, now]);
  const claimLead = (id: string) => setLeads(prev => prev.map(l => l.id === id ? {
    ...l,
    claimedBy: currentUser.id,
    claimedByName: currentUser.name,
    claimedAt: new Date().toISOString()
  } : l));
  const createLoan = (id: string) => setLeads(prev => prev.map(l => l.id === id ? {
    ...l,
    loanCreated: true
  } : l));

  // Manually hand a lead back to the shared queue before the window lapses.
  const releaseLead = (id: string) => setLeads(prev => prev.map(l => l.id === id ? {
    ...l,
    claimedBy: null,
    claimedByName: null,
    claimedAt: null,
    releaseCount: l.releaseCount + 1
  } : l));
  const emptyCopy: Record<QueueTab, string> = {
    available: 'No real-time leads are waiting to be claimed right now.',
    mine: 'You have no claimed leads. Claim one to start working it.',
    all_claims: 'No leads are currently claimed by any loan officer.',
    converted: 'No loans have been created from real-time leads yet.'
  };
  return <Layout data-id="element-483" data-mp="2nhoqidhxgitr">
      <div className="max-w-7xl mx-auto space-y-8" data-id="element-484" data-mp="2ngvr5xlasvln">
        <div data-id="element-485" data-mp="31fve90t5x2fp">
          <div className="flex items-center gap-2" data-id="element-486" data-mp="31fddprzgw21z">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-487" data-mp="31e24vdr60440">
              Real-Time Leads
            </h1>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-id="element-488" data-mp="3g2u62wvxlyuy">
              <span className="relative flex h-2 w-2" data-id="element-489" data-mp="3l15f55eek5c4">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" data-id="element-490" data-mp="372p1eui4hekh" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" data-id="element-491" data-mp="3b2fvp86uyctr" />
              </span>
              Live
            </span>
          </div>
          <p className="text-slate-500 mt-1 flex items-center gap-1.5" data-id="element-492" data-mp="322swmt1lpj3v">
            <Radio size={14} data-id="element-493" data-mp="324vufz8zwgj5" />
            {isAdmin ? `Monitor every officer's claims and conversions · claims lapse after ${CLAIM_WINDOW_HOURS} hours.` : `Borrowers already contacted by the dialer · claim a lead and create a loan within ${CLAIM_WINDOW_HOURS} hours or it returns to the queue.`}
          </p>
        </div>

        <div className={`grid grid-cols-1 gap-6 ${isAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'}`} data-id="element-494" data-mp="2jbjk0awjae82">
          <StatCard title="Available to Claim" value={counts.available} icon={<Hand size={20} data-id="element-496" data-mp="2twax02158s51" />} data-id="element-495" data-mp="2yu2q2zdiykat" />
          {isAdmin && <StatCard title="Active Claims" value={counts.allClaims} icon={<Lock size={20} data-id="element-498" data-mp="2ktrx9qsnmvn0" />} data-id="element-497" data-mp="2ovn57e53c79r" />}
          <StatCard title={isAdmin ? 'Claims Expiring Soon' : 'My Claims Expiring Soon'} value={counts.expiringSoon} icon={<AlertTriangle size={20} data-id="element-500" data-mp="2b48boddk4nog" />} className="border-amber-100 bg-amber-50/30" data-id="element-499" data-mp="2fvqcn1fkyo9f" />
          <StatCard title="Loans Created" value={counts.converted} icon={<CheckCircle2 size={20} data-id="element-502" data-mp="2185ellxkbf8d" />} className="border-emerald-100 bg-emerald-50/30" data-id="element-501" data-mp="266ow9a54i4ux" />
        </div>

        {/* Queue tabs */}
        <div className="border-b border-slate-200" data-id="element-503" data-mp="1s6e6ybjgoybv">
          <nav className="flex gap-6" aria-label="Real-time lead queues" data-id="element-504" data-mp="1s6nwxz4ib5hk">
            {tabs.map(({
            id,
            label
          }) => {
            const active = tab === id;
            const count = id === 'available' ? counts.available : id === 'mine' ? counts.mine : id === 'all_claims' ? counts.allClaims : counts.converted;
            return <button key={id} type="button" onClick={() => setTab(id)} aria-current={active ? 'page' : undefined} className={`relative -mb-px flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${active ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`} data-id="element-505" data-mp="3l41c7zf5465u">
                  {label}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${active ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-506" data-mp="3f4uyrce4asad">
                    {count}
                  </span>
                </button>;
          })}
          </nav>
        </div>

        {isAdmin && tab === 'all_claims' && <div className="space-y-3" data-id="element-507" data-mp="2vej1rc6riom3">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-508" data-mp="30f5ie9xgehpi">
              Claims by Loan Officer
            </h2>
            <ClaimMonitorTable rows={monitorRows} data-id="element-509" data-mp="35e02df75qcb7" />
          </div>}

        <div className="space-y-3" data-id="element-510" data-mp="3a605thmfrncj">
          {isAdmin && tab === 'all_claims' && visible.length > 0 && <h2 className="text-lg font-semibold text-slate-900" data-id="element-511" data-mp="2cqw21nj0em5r">
              Claimed Leads
            </h2>}
          {visible.length > 0 ? visible.map(({
          lead,
          state
        }) => <RealtimeLeadCard key={lead.id} lead={lead} state={state} now={now} onClaim={claimLead} onCreateLoan={createLoan} onRelease={releaseLead} data-id="element-512" data-mp="2a4n2mgah4ad3" />) : <div className="bg-white rounded-xl border border-dashed border-slate-300 py-16 text-center" data-id="element-513" data-mp="1v7m8y78q25bz">
              <p className="text-slate-500" data-id="element-514" data-mp="1r8ddmxy19zeb">{emptyCopy[tab]}</p>
            </div>}
        </div>
      </div>
    </Layout>;
}